/**
 * DBN Shared Utilities, Components, and Data Client
 */
import React from "react";
import client from "@/api/client";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

// ── API helpers ──────────────────────────────────────────────────────────────
export const dbnApi = {
  overview:           () => client.get("/dbn/overview"),
  portfolioHealth:    () => client.get("/dbn/portfolio-health"),
  scenarios:          () => client.get("/dbn/scenarios"),
  facilities:         () => client.get("/dbn/facilities"),
  pfis:               () => client.get("/dbn/pfis"),
  pfi:               (id) => client.get(`/dbn/pfis/${id}`),
  projects:           () => client.get("/dbn/projects"),
  project:           (id) => client.get(`/dbn/projects/${id}`),
  pipeline:           () => client.get("/dbn/pipeline"),
  riskAdditionality:  () => client.get("/dbn/risk-additionality"),
  monetisation:       () => client.get("/dbn/monetisation"),
  reporting:          () => client.get("/dbn/reporting"),
  alerts:             () => client.get("/dbn/alerts"),
  aiInsights:        (q)  => client.get(`/dbn/ai-insights?q=${encodeURIComponent(q)}`),
  aiGenerate:    (payload) => client.post("/dbn/ai/generate", payload),
  resolveAlert:      (id) => client.post(`/dbn/alerts/${id}/resolve`),
  // Assessments
  assessments:        () => client.get("/dbn/assessments"),
  assessment:        (id) => client.get(`/dbn/assessments/${id}`),
  createAssessment: (data) => client.post("/dbn/assessments", data),
  assessmentAction: (id, payload) => client.post(`/dbn/assessments/${id}/action`, payload),
  assessmentMemoPdf: (id)  => `${process.env.REACT_APP_BACKEND_URL}/api/dbn/assessments/${id}/memo`,
  // TA Manager
  taManager:          () => client.get("/dbn/ta-manager"),
  createTa:       (data) => client.post("/dbn/ta-programmes", data),
  updateTa:      (id, data) => client.put(`/dbn/ta-programmes/${id}`, data),
  // Reports
  reportDownload: (id, fmt) => `${process.env.REACT_APP_BACKEND_URL}/api/dbn/reports/${id}/download?fmt=${fmt}`,
  // Excellence Sprint analytics
  portfolioAnalytics: () => client.get("/dbn/portfolio-analytics"),
  pipelineVelocity:   () => client.get("/dbn/pipeline-velocity"),
  riskPar:            () => client.get("/dbn/risk-par"),
  revenueForecast:    () => client.get("/dbn/revenue-forecast"),
  executiveSummary:   () => client.get("/dbn/executive-summary"),
  // Final Sprint
  facilityDetail:     (id) => client.get(`/dbn/facilities/${id}/detail`),
  msmeReadiness:      ()  => client.get("/dbn/msme-readiness"),
  peerBenchmarks:     ()  => client.get("/dbn/peer-benchmarks"),
  // PoA
  poaOverview:          () => client.get("/dbn/poa/overview"),
  poaProgrammes:        () => client.get("/dbn/poa/programmes"),
  poaProgramme:        (id) => client.get(`/dbn/poa/programmes/${id}`),
  poaAggregation: (params) => client.get(`/dbn/poa/aggregation-suggestions?${new URLSearchParams(params||{})}`),
  createPoa:       (data) => client.post("/dbn/poa/programmes", data),
  poaAi:           (data) => client.post("/dbn/poa/ai", data),
  poaReportDownload: (type, fmt) => `${process.env.REACT_APP_BACKEND_URL}/api/dbn/poa/reports/${type}/download?fmt=${fmt}`,
};

// ── Formatters ────────────────────────────────────────────────────────────────
export const fmtEur = (v) => {
  if (!v && v !== 0) return "—";
  const n = Number(v);
  if (n >= 1e9) return `€${(n/1e9).toFixed(1)}B`;
  if (n >= 1e6) return `€${(n/1e6).toFixed(0)}M`;
  if (n >= 1e3) return `€${(n/1e3).toFixed(0)}K`;
  return `€${n.toLocaleString()}`;
};
export const fmtNgn = (v) => {
  if (!v && v !== 0) return "—";
  const n = Number(v) * 1640;
  if (n >= 1e9) return `₦${(n/1e9).toFixed(1)}B`;
  if (n >= 1e6) return `₦${(n/1e6).toFixed(0)}M`;
  if (n >= 1e3) return `₦${(n/1e3).toFixed(0)}K`;
  return `₦${n.toLocaleString()}`;
};
export const fmtUsd = (v) => {
  if (!v && v !== 0) return "—";
  const n = Number(v);
  if (n >= 1e6) return `$${(n/1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n/1e3).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
};
export const fmtN = (v) => {
  if (!v && v !== 0) return "—";
  const n = Number(v);
  if (n >= 1e6) return `${(n/1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n/1e3).toFixed(0)}K`;
  return n.toLocaleString();
};
export const fmtPct = (v, d=1) => v == null ? "—" : `${Number(v).toFixed(d)}%`;

// ── Score Color ───────────────────────────────────────────────────────────────
export const scoreColor = (s) => s >= 80 ? "#22c55e" : s >= 65 ? "#f59e0b" : s >= 50 ? "#f97316" : "#ef4444";
export const riskColor = (r) => r === "Low" ? "#22c55e" : r === "Moderate" ? "#f59e0b" : "#ef4444";
export const eligColor = (s) => s === "Pass" ? "#22c55e" : s === "Conditional" ? "#f59e0b" : "#ef4444";
export const stageBg = (s) => {
  const m = {"Issued":"#14532d","Monitoring":"#1e3a5f","Disbursed":"#1e3a5f","Approved":"#2d1b69",
    "Due Diligence":"#451a03","PFI Review":"#3b1515","Technical Assistance":"#1a2e1a",
    "Readiness Assessment":"#1a1a2e","In Pipeline":"#1a1a2e"};
  return m[s] || "#1e293b";
};

// ── KPI Card ──────────────────────────────────────────────────────────────────
export function KPICard({ label, value, sub, color="#60a5fa", icon: Icon, trend, testId, onClick }) {
  return (
    <div data-testid={testId}
      onClick={onClick}
      style={{
        background:"#0a1628", border:"1px solid #0f2033",
        borderRadius:14, padding:"14px 16px", flex:"1",
        borderTop:`3px solid ${color}`,
        cursor: onClick ? "pointer" : "default",
        transition:"transform .15s, box-shadow .15s",
      }}
      onMouseEnter={e => { if(onClick) { e.currentTarget.style.transform="translateY(-2px)"; e.currentTarget.style.boxShadow="0 4px 20px rgba(0,0,0,.3)"; }}}
      onMouseLeave={e => { e.currentTarget.style.transform=""; e.currentTarget.style.boxShadow=""; }}
      >
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:6 }}>
        <p style={{ fontSize:10, color:"#64748b", textTransform:"uppercase", letterSpacing:"0.1em", fontWeight:700 }}>
          {label}
        </p>
        {Icon && <Icon size={13} color={color} />}
      </div>
      <p style={{ fontSize:22, fontWeight:900, color:"#f0f9ff", lineHeight:1.1, marginBottom:3 }}>{value}</p>
      {sub && <p style={{ fontSize:10, color:"#475569" }}>{sub}</p>}
      {trend !== undefined && (
        <div style={{ display:"flex", alignItems:"center", gap:3, marginTop:4 }}>
          {trend > 0 ? <TrendingUp size={10} color="#22c55e" /> :
           trend < 0 ? <TrendingDown size={10} color="#ef4444" /> :
           <Minus size={10} color="#64748b" />}
          <span style={{ fontSize:9, color: trend > 0 ? "#22c55e" : trend < 0 ? "#ef4444" : "#64748b" }}>
            {trend > 0 ? "+" : ""}{trend}% vs prior quarter
          </span>
        </div>
      )}
    </div>
  );
}

// ── Health Score Card ─────────────────────────────────────────────────────────
export function HealthCard({ label, score, trend, details, status }) {
  const color = score >= 75 ? "#22c55e" : score >= 60 ? "#f59e0b" : "#ef4444";
  const radius = 30, circ = 2 * Math.PI * radius;
  const dash = (score / 100) * circ;
  return (
    <div style={{
      background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:"14px 16px",
      display:"flex", alignItems:"center", gap:14, flex:1
    }}>
      <svg width={72} height={72} style={{ flexShrink:0 }}>
        <circle cx={36} cy={36} r={radius} fill="none" stroke="#0f2033" strokeWidth={6} />
        <circle cx={36} cy={36} r={radius} fill="none" stroke={color} strokeWidth={6}
          strokeDasharray={`${dash} ${circ-dash}`} strokeDashoffset={circ/4}
          strokeLinecap="round" style={{ transition:"stroke-dasharray .6s" }} />
        <text x={36} y={41} textAnchor="middle" fill={color} fontSize={15} fontWeight={900}>{score}</text>
      </svg>
      <div>
        <p style={{ fontSize:10, color:"#64748b", textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:3 }}>{label}</p>
        <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:3 }}>
          <span style={{ fontSize:11, fontWeight:700, color }}>
            {status}
          </span>
          {trend !== undefined && (
            <span style={{ fontSize:9, color: trend >= 0 ? "#22c55e" : "#ef4444" }}>
              {trend >= 0 ? "▲" : "▼"} {Math.abs(trend).toFixed(1)}
            </span>
          )}
        </div>
        <p style={{ fontSize:9, color:"#334155", lineHeight:1.4 }}>{details}</p>
      </div>
    </div>
  );
}

// ── Section Header ────────────────────────────────────────────────────────────
export function SectionHeader({ title, sub, action }) {
  return (
    <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"space-between", marginBottom:14 }}>
      <div>
        <p style={{ fontSize:14, fontWeight:800, color:"#f0f9ff" }}>{title}</p>
        {sub && <p style={{ fontSize:11, color:"#475569", marginTop:2 }}>{sub}</p>}
      </div>
      {action}
    </div>
  );
}

// ── Progress Bar ──────────────────────────────────────────────────────────────
export function ProgressBar({ value, max=100, color="#3b82f6", height=6, label, showPct=true }) {
  const pct = Math.min((Number(value)/Number(max))*100, 100);
  return (
    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
      {label && <span style={{ fontSize:10, color:"#64748b", minWidth:80, flexShrink:0 }}>{label}</span>}
      <div style={{ flex:1, height, background:"#0f2033", borderRadius:4 }}>
        <div style={{ height, width:`${pct}%`, background:color, borderRadius:4, transition:"width .5s" }} />
      </div>
      {showPct && <span style={{ fontSize:10, color:"#94a3b8", width:36, textAlign:"right" }}>{pct.toFixed(0)}%</span>}
    </div>
  );
}

// ── Status Badge ──────────────────────────────────────────────────────────────
export function Badge({ label, color="#60a5fa" }) {
  return (
    <span style={{
      fontSize:9, fontWeight:700, padding:"2px 7px", borderRadius:10,
      background:`${color}20`, color, border:`1px solid ${color}30`,
      textTransform:"uppercase", letterSpacing:"0.08em", whiteSpace:"nowrap"
    }}>{label}</span>
  );
}

// ── Traffic Light ─────────────────────────────────────────────────────────────
export function TrafficLight({ status }) {
  const cfg = {
    "Compliant":{"color":"#22c55e"},"On Track":{"color":"#22c55e"},"Strong":{"color":"#22c55e"},
    "At Risk":{"color":"#f59e0b"},"Needs TA":{"color":"#f59e0b"},"Overdue":{"color":"#ef4444"},
    "Gap Analysis":{"color":"#f59e0b"},"Developing":{"color":"#f59e0b"},"Active":{"color":"#22c55e"},
    "Draft":{"color":"#64748b"},"Published":{"color":"#3b82f6"},"Ready":{"color":"#22c55e"},
    "Good":{"color":"#22c55e"},"Advancing":{"color":"#3b82f6"},"Behind Pace":{"color":"#f59e0b"},
    "Healthy":{"color":"#22c55e"},"Needs Attention":{"color":"#f59e0b"},
  };
  const c = cfg[status] || {color:"#64748b"};
  return (
    <div style={{ display:"flex", alignItems:"center", gap:5 }}>
      <div style={{ width:7, height:7, borderRadius:"50%", background:c.color }} />
      <span style={{ fontSize:10, color:c.color, fontWeight:600 }}>{status}</span>
    </div>
  );
}

// ── Page Header ───────────────────────────────────────────────────────────────
export function PageHeader({ title, subtitle, children }) {
  return (
    <div style={{
      background:"#07111e", borderBottom:"1px solid #0f1e33",
      padding:"14px 24px", flexShrink:0
    }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div>
          <h1 style={{ fontSize:16, fontWeight:900, color:"#f0f9ff", margin:0 }}>{title}</h1>
          {subtitle && <p style={{ fontSize:11, color:"#475569", margin:"2px 0 0" }}>{subtitle}</p>}
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>{children}</div>
      </div>
    </div>
  );
}

// ── Loading State ─────────────────────────────────────────────────────────────
export function LoadingState({ label="Loading data…" }) {
  return (
    <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", padding:40 }}>
      <div style={{ textAlign:"center" }}>
        <div style={{ width:32, height:32, border:"3px solid #0f2033", borderTopColor:"#34d399",
          borderRadius:"50%", animation:"spin 1s linear infinite", margin:"0 auto 12px" }} />
        <p style={{ fontSize:12, color:"#475569" }}>{label}</p>
      </div>
    </div>
  );
}

// ── Btn ───────────────────────────────────────────────────────────────────────
export function Btn({ children, onClick, color="#34d399", outline=false, small=false, disabled=false, testId }) {
  const bg = outline ? "transparent" : `linear-gradient(135deg,${color},${color}cc)`;
  const tc = outline ? color : "#000";
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testId}
      style={{
        padding: small ? "4px 10px" : "6px 14px",
        borderRadius:8, fontSize: small ? 10 : 11, fontWeight:700,
        background:bg, border:`1px solid ${color}`,
        color: outline ? color : "#07111e", cursor:disabled?"not-allowed":"pointer",
        opacity:disabled?0.5:1, transition:"all .15s",
      }}
      onMouseEnter={e => { if(!disabled) e.currentTarget.style.opacity="0.85"; }}
      onMouseLeave={e => { e.currentTarget.style.opacity="1"; }}
    >{children}</button>
  );
}

// ── Modal Shell ───────────────────────────────────────────────────────────────
export function Modal({ open, onClose, title, children, width=640 }) {
  if (!open) return null;
  return (
    <div style={{
      position:"fixed", inset:0, background:"rgba(0,0,0,.75)", zIndex:2000,
      display:"flex", alignItems:"center", justifyContent:"center", padding:24
    }} onClick={e => { if(e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background:"#0a1628", border:"1px solid #1e3a5f", borderRadius:16,
        width:"100%", maxWidth:width, maxHeight:"90vh", overflowY:"auto",
        boxShadow:"0 24px 64px rgba(0,0,0,.8)"
      }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
          padding:"16px 20px", borderBottom:"1px solid #0f2033" }}>
          <p style={{ fontSize:14, fontWeight:800, color:"#f0f9ff" }}>{title}</p>
          <button onClick={onClose} style={{ background:"none", border:"none", color:"#475569",
            cursor:"pointer", fontSize:18, lineHeight:1 }}>✕</button>
        </div>
        <div style={{ padding:"16px 20px" }}>{children}</div>
      </div>
    </div>
  );
}

// ── Score Ring ────────────────────────────────────────────────────────────────
export function ScoreRing({ score, size=60, strokeWidth=5 }) {
  const color = scoreColor(score);
  const radius = (size - strokeWidth * 2) / 2;
  const circ = 2 * Math.PI * radius;
  const dash = (score / 100) * circ;
  return (
    <svg width={size} height={size}>
      <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#0f2033" strokeWidth={strokeWidth} />
      <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={color} strokeWidth={strokeWidth}
        strokeDasharray={`${dash} ${circ-dash}`} strokeDashoffset={circ/4}
        strokeLinecap="round" />
      <text x={size/2} y={size/2+4} textAnchor="middle" fill={color}
        fontSize={size * 0.25} fontWeight={900}>{score}</text>
    </svg>
  );
}
